import { useId, useState } from "react";
import { ApiError, SCHEDULE_MAX_DAYS_AHEAD, type IfNoReply, type ScheduledMessage, type ScheduleMessageRequest } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Button, Dialog, DialogContent, DialogTitle, RadioChip, Textarea, TextInput } from "@ferrivia/ui";
import {
  DEFAULT_REPLY_BY_DAYS,
  QUICK_PICK_DAYS,
  addDays,
  messageText,
  parseLocal,
  quickPickDate,
  sendAtProblem,
  toDateInput,
  toTimeInput,
  type WhenChoice,
} from "./schedule";
import s from "./ScheduleDialog.module.css";

export interface ScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when editing or rescheduling; absent when creating from the composer draft. */
  existing?: ScheduledMessage;
  initialText?: string;
  clientName: string;
  staffName: string;
  onSubmit: (req: ScheduleMessageRequest) => Promise<void>;
}

type FormProps = Omit<ScheduleDialogProps, "open">;

function ScheduleForm({ onOpenChange, existing, initialText = "", clientName, staffName, onSubmit }: FormProps) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();
  const uid = useId();
  const [now] = useState(() => new Date());
  const existingSendAt = existing ? new Date(existing.sendAt) : null;

  const [text, setText] = useState(existing ? messageText(existing) : initialText);
  const [choice, setChoice] = useState<WhenChoice>(existingSendAt ? "custom" : "1w");
  const [date, setDate] = useState(existingSendAt ? toDateInput(existingSendAt) : "");
  const [time, setTime] = useState(existingSendAt ? toTimeInput(existingSendAt) : "09:00");
  const [ifNoReply, setIfNoReply] = useState<IfNoReply>(existing?.ifNoReply ?? "remind");
  const [days, setDays] = useState(String(existing?.replyByDays ?? DEFAULT_REPLY_BY_DAYS));
  const [nudgeText, setNudgeText] = useState(existing?.nudgeText ?? "");
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const sendAt = choice === "custom" ? parseLocal(date, time) : quickPickDate(QUICK_PICK_DAYS[choice], now);
  const problem = sendAtProblem(sendAt, now);
  const daysNumber = Number(days);
  const daysValid = Number.isInteger(daysNumber) && daysNumber >= 1 && daysNumber <= 60;
  const nudgeMissing = ifNoReply === "nudge" && nudgeText.trim() === "";
  const canSubmit =
    text.trim() !== "" && problem === null && (ifNoReply === "none" || daysValid) && !nudgeMissing && !saving;

  const dateTimeFormat = new Intl.DateTimeFormat(tag, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const dayFormat = new Intl.DateTimeFormat(tag, { weekday: "short", day: "numeric", month: "short" });

  const title = !existing
    ? t("customerWorkspace.scheduled.dialog.titleCreate", "Send later")
    : existing.state === "held"
      ? t("customerWorkspace.scheduled.dialog.titleReschedule", "Reschedule")
      : t("customerWorkspace.scheduled.dialog.titleEdit", "Edit scheduled message");

  const choices: { id: WhenChoice; label: string }[] = [
    { id: "3d", label: t("customerWorkspace.scheduled.dialog.in3Days", "In 3 days") },
    { id: "1w", label: t("customerWorkspace.scheduled.dialog.in1Week", "In 1 week") },
    { id: "2w", label: t("customerWorkspace.scheduled.dialog.in2Weeks", "In 2 weeks") },
    { id: "custom", label: t("customerWorkspace.scheduled.dialog.pickDate", "Pick a date…") },
  ];
  const replyOptions: { id: IfNoReply; label: string }[] = [
    { id: "none", label: t("customerWorkspace.scheduled.dialog.ifNone", "Nothing") },
    { id: "remind", label: t("customerWorkspace.scheduled.dialog.ifRemind", "Remind me") },
    { id: "nudge", label: t("customerWorkspace.scheduled.dialog.ifNudge", "Send a nudge") },
  ];

  async function submit() {
    if (!canSubmit || !sendAt) {
      return;
    }
    const req: ScheduleMessageRequest = {
      body: { v: 1, blocks: [{ type: "text", text: text.trim() }] },
      sendAt: sendAt.toISOString(),
      ifNoReply,
      ...(ifNoReply !== "none" ? { replyByDays: daysNumber } : {}),
      ...(ifNoReply === "nudge" ? { nudgeText: nudgeText.trim() } : {}),
    };
    setSaving(true);
    setErrorKey(null);
    try {
      await onSubmit(req);
      onOpenChange(false);
    } catch (err) {
      setErrorKey(err instanceof ApiError ? err.code : "failed");
      setSaving(false);
    }
  }

  const errorText =
    errorKey === null
      ? null
      : errorKey === "invalid_send_at"
        ? t("customerWorkspace.scheduled.dialog.errorSendAt", {
            days: SCHEDULE_MAX_DAYS_AHEAD,
            defaultValue: "That send time isn't allowed. Pick a time within the next {{days}} days.",
          })
        : errorKey === "not_author"
          ? t("customerWorkspace.scheduled.dialog.errorNotAuthor", "Only the author can change this message.")
          : errorKey === "not_pending"
            ? t("customerWorkspace.scheduled.dialog.errorNotPending", "This message was already sent or cancelled.")
            : t("customerWorkspace.scheduled.dialog.errorFailed", "Couldn't save. Try again.");

  const problemText =
    problem === "past"
      ? t("customerWorkspace.scheduled.dialog.pastTime", "That time has passed. Pick a new one.")
      : problem === "tooFar"
        ? t("customerWorkspace.scheduled.dialog.tooFar", {
            days: SCHEDULE_MAX_DAYS_AHEAD,
            defaultValue: "Pick a time within the next {{days}} days.",
          })
        : null;

  return (
    <>
      <DialogTitle className={s.title}>{title}</DialogTitle>

      <div className={s.field}>
        <label htmlFor={`${uid}-message`} className={s.label}>
          {t("customerWorkspace.scheduled.dialog.message", "Message")}
        </label>
        <Textarea
          id={`${uid}-message`}
          value={text}
          rows={6}
          maxHeight={360}
          className={s.message}
          onChange={(event) => setText(event.target.value)}
        />
      </div>

      <div className={s.field}>
        <span id={`${uid}-when`} className={s.label}>
          {t("customerWorkspace.scheduled.dialog.when", "When")}
        </span>
        <div role="radiogroup" aria-labelledby={`${uid}-when`} className={s.chips}>
          {choices.map((option) => (
            <RadioChip
              key={option.id}
              name={`${uid}-when`}
              checked={choice === option.id}
              onChange={() => setChoice(option.id)}
            >
              {option.label}
            </RadioChip>
          ))}
        </div>
        {choice === "custom" ? (
          <div className={s.pickers}>
            <TextInput
              type="date"
              aria-label={t("customerWorkspace.scheduled.dialog.date", "Date")}
              value={date}
              min={toDateInput(now)}
              max={toDateInput(addDays(now, SCHEDULE_MAX_DAYS_AHEAD))}
              onChange={(event) => setDate(event.target.value)}
            />
            <TextInput
              type="time"
              aria-label={t("customerWorkspace.scheduled.dialog.time", "Time")}
              value={time}
              onChange={(event) => setTime(event.target.value)}
            />
          </div>
        ) : null}
        <p className={s.resolved}>
          {sendAt && problem !== "missing"
            ? t("customerWorkspace.scheduled.dialog.sendsOn", {
                when: dateTimeFormat.format(sendAt),
                defaultValue: "Sends {{when}}",
              })
            : null}
        </p>
        {problemText ? (
          <p className={s.hint} role="alert">
            {problemText}
          </p>
        ) : null}
      </div>

      <div className={s.field}>
        <span id={`${uid}-reply`} className={s.label}>
          {t("customerWorkspace.scheduled.dialog.ifNoReply", {
            name: clientName,
            defaultValue: "If {{name}} hasn't replied",
          })}
        </span>
        <div role="radiogroup" aria-labelledby={`${uid}-reply`} className={s.chips}>
          {replyOptions.map((option) => (
            <RadioChip
              key={option.id}
              name={`${uid}-reply`}
              checked={ifNoReply === option.id}
              onChange={() => setIfNoReply(option.id)}
            >
              {option.label}
            </RadioChip>
          ))}
        </div>
        {ifNoReply !== "none" ? (
          <div className={s.byDays}>
            <label htmlFor={`${uid}-days`} className={s.hint}>
              {t("customerWorkspace.scheduled.dialog.daysAfterSending", "Days after sending")}
            </label>
            <TextInput
              id={`${uid}-days`}
              type="number"
              inputMode="numeric"
              min={1}
              max={60}
              value={days}
              className={s.days}
              onChange={(event) => setDays(event.target.value)}
            />
            {sendAt && problem === null && daysValid ? (
              <span className={s.hint}>
                {t("customerWorkspace.scheduled.dialog.noReplyBy", {
                  date: dayFormat.format(addDays(sendAt, daysNumber)),
                  defaultValue: "No reply by {{date}}",
                })}
              </span>
            ) : null}
          </div>
        ) : null}
        {ifNoReply === "nudge" ? (
          <>
            <label htmlFor={`${uid}-nudge`} className={s.label}>
              {t("customerWorkspace.scheduled.dialog.nudgeText", "Nudge message")}
            </label>
            <Textarea
              id={`${uid}-nudge`}
              value={nudgeText}
              rows={3}
              maxHeight={200}
              className={s.message}
              maxLength={1000}
              aria-describedby={`${uid}-nudge-hint`}
              onChange={(event) => setNudgeText(event.target.value)}
            />
            <p id={`${uid}-nudge-hint`} className={s.hint}>
              {t("customerWorkspace.scheduled.dialog.nudgeHint", {
                name: clientName,
                defaultValue: "Sent once, in your name, only if {{name}} hasn't written by then.",
              })}
            </p>
          </>
        ) : null}
      </div>

      {errorText ? (
        <p className={s.error} role="alert">
          {errorText}
        </p>
      ) : null}

      <div className={s.footer}>
        <span className={s.sentAs}>
          {t("customerWorkspace.scheduled.dialog.sentAs", {
            name: staffName,
            defaultValue: "Sent as {{name}}",
          })}
        </span>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t("customerWorkspace.scheduled.dialog.cancel", "Cancel")}
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {existing
            ? t("customerWorkspace.scheduled.dialog.save", "Save")
            : t("customerWorkspace.scheduled.dialog.schedule", "Schedule")}
        </Button>
      </div>
    </>
  );
}

export function ScheduleDialog({ open, onOpenChange, ...form }: ScheduleDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={s.content} aria-describedby={undefined}>
        <ScheduleForm onOpenChange={onOpenChange} {...form} />
      </DialogContent>
    </Dialog>
  );
}
